import { ConsumerAnalyticsService, FlowStatus, logger } from '@upflowy/logger';
import { CONSUMER_APP_SOURCE } from '@upflowy/types';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useFlowVersion } from '../contexts/Version';
import { EVENT_TYPE_REDIRECT, interpolateMustachifiedHTML } from '../helpers';
import { useApp } from '../providers/app-provider';
import { useFlowConfig } from '../providers/flow-config-provider';
import { useStateMachine } from '../providers/state-machine-provider';
import { onStepExit, onStepReady } from '../services/upflowy-plugin';
import { StepAssetType } from '../types';

const DISMISS_CONTAINER_CSS = '.UP_dismissContainer';
const BACK_BTN_CONTAINER_CSS = '.UP_backButtonContainer';
const WATERMARK_CONTAINER_CSS = '.UP_waterMark';
const WATERMARK_LINK_CSS = '.UP_waterMark a';

type InnerHtmlProps = {
  html: string | undefined;
  context: Record<string, unknown>;
  stepId: string | number;
  stepTitle: string | undefined;
  state: string;
  style: React.CSSProperties;
  assets: StepAssetType[];
};

const InnerHTML = ({ html, context, stepTitle, stepId, state, style, assets }: InnerHtmlProps) => {
  const { hasStepOnBackBtn, errorMessage } = useStateMachine();
  const { isStandaloneApp, user, containerId, origin } = useApp();
  const divRef = useRef<HTMLDivElement>(null);
  const htmlRef = useRef<HTMLDivElement>(null);
  const [htmlContent, setHtmlContent] = useState<string>();
  const stepClassName = `step-${stepId}`;
  const { flow, showWatermark } = useFlowConfig();

  const { dependenciesLoaded } = useFlowVersion();

  const handleClick = useCallback(
    (link: string) => {
      window.parent.postMessage(
        {
          source: CONSUMER_APP_SOURCE,
          containerId,
          redirectUrl: link,
          type: EVENT_TYPE_REDIRECT,
        },
        origin ?? '',
      );
    },
    [containerId, origin],
  );

  useEffect(() => {
    const content = html ? interpolateMustachifiedHTML(html, context) : '';
    setHtmlContent(content);
  }, [context, html]);

  useEffect(() => {
    if (dependenciesLoaded && htmlRef.current && assets.length) {
      const assetsString = assets.reduce((memo, asset) => {
        switch (asset.type) {
          case 'css-inline': {
            return (
              `${memo}<style data-u-asset-type="${asset.assetType}" data-u-asset-id="${asset.id}">` +
              `${asset.css}</style>`
            );
          }
          case 'js-inline': {
            return `${memo}<script type="text/javascript" data-u-asset-id="${asset.id}">${asset.script}</script>`;
          }
          default:
            logger.warn(`Unknown asset type ${asset}`, { flowId: flow?.id });
            return memo;
        }
      }, '');

      const slotHtml = document
        .createRange()
        .createContextualFragment(`${assetsString}${htmlContent}`);
      if (isStandaloneApp) {
        const dismissContainer = slotHtml.querySelector<HTMLElement>(DISMISS_CONTAINER_CSS);
        if (dismissContainer) dismissContainer.style.visibility = 'hidden';
      }
      if (!hasStepOnBackBtn) {
        const backButtonContainer = slotHtml.querySelector<HTMLElement>(BACK_BTN_CONTAINER_CSS);
        if (backButtonContainer) backButtonContainer.style.visibility = 'hidden';
      }
      if (!showWatermark) {
        const showWaterMarkContainer = slotHtml.querySelector<HTMLElement>(WATERMARK_CONTAINER_CSS);
        if (showWaterMarkContainer) showWaterMarkContainer.style.visibility = 'hidden';
      } else {
        const showWaterMarkLink = slotHtml.querySelector<HTMLElement>(WATERMARK_LINK_CSS);
        if (showWaterMarkLink) {
          const link =
            'https://www.upflowy.com/?utm_source=flow&utm_medium=referral' +
            `&utm_campaign=${flow?.organisationId}&utm_id=${user?.uid}&utm_term=${stepId}&utm_content=${flow?.id}`;
          showWaterMarkLink.setAttribute('href', link);
          showWaterMarkLink.setAttribute('target', '_blank');
          if (!isStandaloneApp) {
            showWaterMarkLink.addEventListener('click', (e) => {
              e.preventDefault();
              handleClick(link);
            });
          }
        }
      }
      htmlRef.current.innerHTML = '';
      htmlRef.current.appendChild(slotHtml);

      // Focus field if first, and if "focus-elligible"
      try {
        const elligibleParentToFocus =
          '.UP_formContainer > .form-focus-eligible:not(.form-input-element + .form-input-element)';
        const chosenInputToFocus: HTMLElement | null = htmlRef.current.querySelector(
          `${elligibleParentToFocus} input,${elligibleParentToFocus} textarea`,
        );
        const nextTickTimeout = setTimeout(() => {
          if (
            chosenInputToFocus &&
            chosenInputToFocus.getBoundingClientRect().top < window.innerHeight
          ) {
            chosenInputToFocus.focus({
              preventScroll: true,
            });
          }
          clearTimeout(nextTickTimeout);
        }, 10);
      } catch {
        logger.warn('No .focus() on this browser (likely due to css :not() unavailable)');
      }

      onStepReady(stepId, htmlRef.current);
      ConsumerAnalyticsService.endTransaction(FlowStatus.FOUND);
    }
    return () => {
      stepId && onStepExit(stepId);
    };
  }, [
    assets,
    dependenciesLoaded,
    flow?.id,
    flow?.organisationId,
    handleClick,
    hasStepOnBackBtn,
    htmlContent,
    isStandaloneApp,
    showWatermark,
    stepId,
    user?.uid,
  ]);

  /* eslint-disable react/no-danger */
  // Adding externally provided HTML from step data
  return (
    <div>
      <div
        className={stepClassName}
        data-title={stepTitle}
        data-state={state}
        data-step-id={stepId}
        ref={divRef}
        style={{ height: 'inherit', ...(style || {}) }}
      />
      <div ref={htmlRef} />
      {errorMessage && <div className="globalErrorMessage">{errorMessage}</div>}
    </div>
  );
};

export default InnerHTML;
